import { Button, Card, Col, Container, ListGroup, ListGroupItem, Pagination, Row } from "react-bootstrap";
import { AppAuthStateProps } from '../../types/propTypes';
import { faEye, faFilter, faRocket } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import pLimit from "p-limit";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FhirPatientVisit, callFhirPatient } from "../../amplify/appsync/fetch-sync-fhir";
import { Favorite, Note, PATIENT_VISITS_WITH_METADATA_BY_ARRIVAL, callQuery } from "../../amplify/appsync/query";
import { PatientVisit, PatientVisitMetadata } from '../../amplify/appsync/search-patients';
import { getRandomFirstName, getRandomLastName, isDemoUser } from "../../utils/demo";
import { FavoriteColumnSelectModal } from "./FavoriteColumnSelectModal";
import { FavoriteContextMenu } from "./FavoriteContextMenu";
import { FavoriteNewListModal } from "./FavoriteNewListModal";
import { getSqlDateStrFromDateStr } from "../../utils/time";
import { useGlobalOptions } from "../common/GlobalOptionsContext";
import { DateType } from "../filters/dateTypes";
import { FilterTimeSelector } from "./FilterTimeSelector";
import { DEFAULT_SEARCH_COLUMNS_V2 } from "./trackboardUtils";
import { TrackboardV2Item } from "./TrackboardV2Item";
import { UserRole } from "../../utils/user";

const MAX_RESULTS_TO_DISPLAY = 30;

export interface SearchMetadata {
    full_count: number | null,
    is_favorited: boolean,
}

export const SearchDashboard = (props: AppAuthStateProps) => {
    const { getDerivedRole } = useGlobalOptions();
    const [searchParams, setSearchParams] = useSearchParams();

    const [isLoading, setIsLoading] = useState(false)
    const [allPatients, setAllPatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata & SearchMetadata)[]>([]);
    const [activePatients, setActivePatients] = useState<(PatientVisit & FhirPatientVisit & PatientVisitMetadata & SearchMetadata)[]>([]);
    const [mrnToName, setMrnToName] = useState<any>({});
    const [csnToFavorites, setCsnToFavorites] = useState<any>({});
    const [csnToReviewed, setCsnToReviewed] = useState<any>({});
    
    // Track if there are more results for next/prev navigation
    const [hasNextPage, setHasNextPage] = useState<boolean>(false);
    const [hasPrevPage, setHasPrevPage] = useState<boolean>(false);
    
    const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
    const [contextMenu, setContextMenu] = useState<any>({x: 0, y: 0, patient: null, listName: null});
    const [showNewList, setShowNewList] = useState<boolean>(false);
    const [favoriteLists, setFavoriteLists] = useState<string[]>([]);
    const [filterShowDateOptionsArrived, setFilterShowDateOptionsArrived] = useState<boolean>(false);

    // Initialize state from URL params
    const getInitialDateOptions = () => {
        const urlStart = searchParams.get('start');
        const urlEnd = searchParams.get('end');
        
        const defaultStart = new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7);
        const defaultEnd = new Date();
        
        return {
            dateOptionType: DateType.Absolute,
            relativeStart: null,
            relativeStartScale: "d",
            relativeEnd: 0,
            relativeEndScale: "d",
            absoluteStart: urlStart ? new Date(urlStart) : defaultStart,
            absoluteEnd: urlEnd ? new Date(urlEnd) : defaultEnd,
        };
    };

    const [filterDateOptionsArrived, setFilterDateOptionsArrived]: any = useState<any>(getInitialDateOptions());
    
    const [showColumnSelect, setShowColumnSelect] = useState<boolean>(false);
    const [columns, setColumns] = useState<any[]>((() => {
        const viewableColumns = DEFAULT_SEARCH_COLUMNS_V2.filter((c) => c.show);
        if (getDerivedRole(props.user) === UserRole.ADMIN) {
            return viewableColumns;
        } else {
            return viewableColumns.filter((col) => col.id !== "has_evaluate_capacity_output");
        }
    })());

    // Get page from URL params
    const activePageNumber = parseInt(searchParams.get('page') || '1', 10);

    // Update URL params when filters change
    useEffect(() => {
        const params: any = {
            page: activePageNumber.toString(),
        };
        
        if (filterDateOptionsArrived.absoluteStart) {
            params.start = filterDateOptionsArrived.absoluteStart.toISOString();
        }
        if (filterDateOptionsArrived.absoluteEnd) {
            params.end = filterDateOptionsArrived.absoluteEnd.toISOString();
        }
        
        setSearchParams(params, { replace: true });
    }, [activePageNumber, filterDateOptionsArrived]);

    useEffect(() => {
        refresh();
    }, [activePageNumber, filterDateOptionsArrived]);

    useEffect(() => {
        setAllPatients([
            ...allPatients.map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata & SearchMetadata)) => {
                return {
                    ...patient,
                    given: mrnToName[patient.mrn] ? mrnToName[patient.mrn].given : "",
                    family: mrnToName[patient.mrn] ? mrnToName[patient.mrn].family : "",
                }
            })),
        ])
        setActivePatients([
            ...allPatients.map(((patient: (PatientVisit & FhirPatientVisit & PatientVisitMetadata & SearchMetadata)) => {
                return {
                    ...patient,
                    given: mrnToName[patient.mrn] ? mrnToName[patient.mrn].given : "",
                    family: mrnToName[patient.mrn] ? mrnToName[patient.mrn].family : "",
                }
            })),
        ])
    }, [mrnToName]);

    const refresh = () => {
        setIsLoading(true);

        const filters = {
            operation: PATIENT_VISITS_WITH_METADATA_BY_ARRIVAL,
            // Request one extra result to check if there's a next page
            limit: MAX_RESULTS_TO_DISPLAY + 1,
            offset: (activePageNumber - 1) * MAX_RESULTS_TO_DISPLAY,
            filters: {
                arrival_start: filterDateOptionsArrived.absoluteStart ? getSqlDateStrFromDateStr(filterDateOptionsArrived.absoluteStart) : null,
                arrival_end: filterDateOptionsArrived.absoluteEnd ? getSqlDateStrFromDateStr(filterDateOptionsArrived.absoluteEnd) : null,
            },
        };

        callQuery(filters)
        .then((patients: any[]) => {
            setIsLoading(false);
            if (!patients) {
                return;
            }
            
            // Check if there are more results
            const hasMore = patients.length > MAX_RESULTS_TO_DISPLAY;
            setHasNextPage(hasMore);
            setHasPrevPage(activePageNumber > 1);
            
            // Trim to actual display count
            const displayPatients = hasMore ? patients.slice(0, MAX_RESULTS_TO_DISPLAY) : patients;
            
            const csnToFavs: any = {};
            for (let pt of displayPatients) {
                if (pt.is_favorited) {
                    if (!(pt.csn in csnToFavs)) {
                        csnToFavs[pt.csn] = [];
                    }
                    csnToFavs[pt.csn].push(pt);
                }
            }
            setCsnToFavorites(csnToFavs);

            setAllPatients(displayPatients);
            setActivePatients(displayPatients);

            if (!isDemoUser(props.user)) {
                loadPatientNames(displayPatients).then(() => {});
            } else {
                const mrnToName: any = {};
                for (let patient of displayPatients) {
                    mrnToName[patient.mrn] = {
                        given: getRandomFirstName(),
                        family: getRandomLastName(),
                    }
                }
                setMrnToName(mrnToName);
            }
        });
    }

    const loadPatientNames = async (patients: PatientVisit[]) => {
        const limit = pLimit(2);
        const localMrnToName: any = {
            ...mrnToName,
        };

        const promises: any[] = [];
        for (let patient of patients) {
            const mrn = patient.mrn;
            if (!(mrn in localMrnToName)) {
                promises.push(limit(() => {
                    return new Promise((resolve: any, reject: any) => {
                        callFhirPatient({
                            mrn: patient.mrn,
                        }).then((pt: FhirPatientVisit) => {
                            localMrnToName[patient.mrn] = pt;
                            if (Object.keys(localMrnToName).length % 10 === 0) {
                                setMrnToName({
                                    ...localMrnToName
                                });
                            }
                            resolve(pt)
                        }).catch((err: any) => {
                            reject({});
                        })
                    })
                }))
            }
        }

        const result = await Promise.all(promises);
        for (let fhirPatientInfo of result) {
            localMrnToName[fhirPatientInfo.mrn] = fhirPatientInfo;
        }
        setMrnToName(localMrnToName);
    }

    const pageUpdate = (nextPage: number) => {
        const params: any = {
            page: nextPage.toString(),
        };
        
        if (filterDateOptionsArrived.absoluteStart) {
            params.start = filterDateOptionsArrived.absoluteStart.toISOString();
        }
        if (filterDateOptionsArrived.absoluteEnd) {
            params.end = filterDateOptionsArrived.absoluteEnd.toISOString();
        }
        
        setSearchParams(params);
    };

    const handleClear = () => {
        setShowContextMenu(false);
    };

    const updateCsnToFavorites = (csn: string, favoriteType: string, favorite?: Favorite) => {
        const map: any = {...csnToFavorites};
        let newActivePatients = [...activePatients];
        if (!(csn in map)) {
            map[csn] = [];
        }
        if (favorite) {
            map[csn] = [];
            map[csn].push(favorite);
        } else {
            map[csn] = [];
            if (map[csn].length === 0) {
                newActivePatients = newActivePatients.filter((pt: any) => pt.csn !== csn);
            }
        }
        setCsnToFavorites(map);
        setActivePatients(newActivePatients);
    }

    const updatePatientWithNote = (note: Note) => {
        setActivePatients(activePatients =>
            activePatients.map(pt => 
                pt.csn === note.csn ? { ...pt, note: note.note } : pt
            )
        );
    };

    const handleFilterDateChange = (newDateOptions: any) => {
        setFilterDateOptionsArrived(newDateOptions);
        // Reset to page 1 when filters change
        const params: any = {
            page: '1',
        };
        if (newDateOptions.absoluteStart) {
            params.start = newDateOptions.absoluteStart.toISOString();
        }
        if (newDateOptions.absoluteEnd) {
            params.end = newDateOptions.absoluteEnd.toISOString();
        }
        setSearchParams(params);
    };

    return (
        <>
            <Container style={{ paddingTop: isDemoUser(props.user) ? '143px' : '64px', paddingLeft: '32px', paddingRight: '32px' }} onClick={handleClear} fluid>

                <Row style={{marginBottom: '12px'}}>
                    <Col md={9}>
                        <div style={{display: 'inline-block', position: 'relative'}}>
                            <div className="mb-2">
                                <strong><FontAwesomeIcon icon={faFilter} /> Historic Search</strong>
                            </div>
                            <FilterTimeSelector
                                filterDateOptions={filterDateOptionsArrived}
                                setFilterDateOptions={handleFilterDateChange}
                                filterDateSelectorShow={filterShowDateOptionsArrived}
                                setFilterDateSelectorShow={setFilterShowDateOptionsArrived}
                                placeholderText={"Arrived At"}
                                maxRangeInDays={7}
                            />
                        </div>
                    </Col>
                    <Col md={3} className="text-right">
                    </Col>
                </Row>
                <Row style={{marginBottom: '24px'}}>
                    <Col>
                        <Card className="trackboard-table">
                            <ListGroup variant="flush">
                                <ListGroupItem className="d-flex flex-column trackboard-table-header">
                                    <div style={{display: 'block', width: '100%'}} >
                                        {
                                            columns.map((col) => {
                                                const totalRelativeCols = columns.reduce((acc, obj) => acc + (obj.size ?? 0), 0);
                                                if (col.id === "action") {
                                                    return (
                                                        <div key={col.id} className="text-right" style={{display: 'inline-block', width: `${100 * col.size/totalRelativeCols}%`}}>
                                                            <Button
                                                                variant="primary"
                                                                className={"trackboard-show-btn"}
                                                                onClick={() => setShowColumnSelect(true)}
                                                            >
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </Button>
                                                        </div>
                                                    )
                                                } else {
                                                    return (
                                                        <div key={col.id} style={{display: 'inline-block', width: `${100 * col.size/totalRelativeCols}%`}}>{col.name}</div>
                                                    )
                                                }
                                            })
                                        }
                                    </div>
                                </ListGroupItem>
                                {
                                    isLoading ? (
                                        <>
                                            {
                                                [1, 2, 3].map((v) => (
                                                    <TrackboardV2Item 
                                                        key={v}
                                                        /** @ts-ignore */
                                                        patient={null}
                                                        columns={columns} 
                                                        csnToFavorites={csnToFavorites}
                                                        csnToReviewed={csnToReviewed}
                                                        pastFavorites={[]}
                                                        pastReviewed={[]}
                                                        setCsnToReviewed={setCsnToReviewed}
                                                        setShowContextMenu={setShowContextMenu}
                                                        setContextMenu={setContextMenu}
                                                        updatePatientWithNote={updatePatientWithNote}
                                                        padNotes={true}
                                                        isPlaceholder={true}
                                                        tenant={props.tenant}
                                                        user={props.user}
                                                    />
                                                ))
                                            }
                                        </>
                                    ) : (
                                        <>{activePatients.map((pt) => (
                                            <TrackboardV2Item 
                                                key={`ti-${pt.csn}`}
                                                patient={pt}
                                                columns={columns} 
                                                csnToFavorites={csnToFavorites}
                                                csnToReviewed={csnToReviewed}
                                                pastFavorites={[]}
                                                pastReviewed={[]}
                                                setCsnToReviewed={setCsnToReviewed}
                                                setShowContextMenu={setShowContextMenu}
                                                setContextMenu={setContextMenu}
                                                updatePatientWithNote={updatePatientWithNote}
                                                tenant={props.tenant}
                                                user={props.user}
                                            />
                                        ))}</>
                                    )
                                }
                            </ListGroup>
                        </Card>
                    </Col>
                </Row>
                {
                    !isLoading && activePatients.length === 0 && (
                        <Container fluid style={{paddingTop: '26px', textAlign: 'center'}}>
                            <Row>
                                <Col md={12}>
                                    <h1 style={{color: '#AAA', fontSize: '64px'}}><FontAwesomeIcon icon={faRocket} /></h1>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <h1 style={{color: '#AAA', fontSize: '15px'}}>No patient visits found matching criteria</h1>
                                </Col>
                            </Row>
                        </Container>
                    )
                }
                <FavoriteContextMenu show={showContextMenu} contextMenu={contextMenu} lists={favoriteLists} setShow={setShowContextMenu} setShowNewList={setShowNewList} updateCsnToFavorites={updateCsnToFavorites} />
                <FavoriteNewListModal show={showNewList} setShow={setShowNewList} lists={favoriteLists} setLists={setFavoriteLists} contextMenu={contextMenu} updateCsnToFavorites={updateCsnToFavorites} />
                <FavoriteColumnSelectModal show={showColumnSelect} setShow={setShowColumnSelect} columns={columns} setColumns={setColumns} allColumns={DEFAULT_SEARCH_COLUMNS_V2} saveKey="search_columns" />

                <Row>
                    <Col>  
                        <Pagination>
                            <Pagination.Prev 
                                disabled={!hasPrevPage || isLoading}
                                onClick={() => hasPrevPage && pageUpdate(activePageNumber - 1)} 
                            />
                            <Pagination.Item active>{activePageNumber}</Pagination.Item>
                            <Pagination.Next 
                                disabled={!hasNextPage || isLoading}
                                onClick={() => hasNextPage && pageUpdate(activePageNumber + 1)} 
                            />
                        </Pagination>
                        <div style={{fontSize: '14px', color: '#666', marginTop: '8px'}}>
                            Page {activePageNumber} • Showing {activePatients.length} results
                        </div>
                    </Col>
                </Row>
            </Container>
        </>
    );
};
